import React from 'react';
import { View, Text, StyleSheet, Pressable, Image } from 'react-native';
import { webColors, webBorderRadius, webShadows } from '../../constants/webStyles';
import { getWorldCountryData } from '../../constants/worldCountries';

const WebJobCard = ({
  job,
  onPress,
  variant = 'default',
  showApplyButton = true,
  onApply,
}) => {
  const {
    title,
    facility,
    facilityLogo,
    location,
    jobType,
    salary,
    postedDate,
    urgency,
    specialization,
    subtitle,
    description,
    applicants,
  } = job;

  const getJobTypeBadgeStyle = () => {
    switch (jobType?.toLowerCase()) {
      case 'locum':
        return styles.badgeLocum;
      case 'full-time':
        return styles.badgeFullTime;
      case 'part-time':
        return styles.badgePartTime;
      default:
        return styles.badgeDefault;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const now = new Date();
    const posted = new Date(date);
    const diffDays = Math.floor((now - posted) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return posted.toLocaleDateString();
  };

  if (variant === 'compact') {
    return (
      <Pressable
        onPress={onPress}
        style={({ hovered }) => [
          styles.cardCompact,
          hovered && styles.cardHover,
        ]}
      >
        <View style={styles.compactContent}>
          <Text style={styles.compactTitle} numberOfLines={1}>
            {title}
          </Text>
          <Text style={styles.compactFacility} numberOfLines={1}>
            {facility}
          </Text>
          <View style={styles.compactMeta}>
            <Text style={styles.compactLocation}>{location}</Text>
            <View style={[styles.badge, getJobTypeBadgeStyle()]}>
              <Text style={styles.badgeText}>{jobType}</Text>
            </View>
          </View>
        </View>
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => [
        styles.card,
        hovered && styles.cardHover,
      ]}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.facilityInfo}>
          {facilityLogo ? (
            <Image source={{ uri: facilityLogo }} style={styles.facilityLogo} />
          ) : (
            <View style={styles.facilityLogoPlaceholder}>
              <Text style={styles.facilityLogoText}>
                {facility?.charAt(0) || 'F'}
              </Text>
            </View>
          )}
          <View style={styles.facilityDetails}>
            <Text style={styles.facilityName}>{facility}</Text>
            <Text style={styles.location}>{location}</Text>
          </View>
        </View>
        <View style={styles.badges}>
          <View style={[styles.badge, getJobTypeBadgeStyle()]}>
            <Text style={styles.badgeText}>{jobType}</Text>
          </View>
          {job.international && (
            <View style={[styles.badge, styles.badgeInternational]}>
              <Text style={styles.badgeText}>
                {`${getWorldCountryData(job.international)?.flag || ''} ${getWorldCountryData(job.international)?.name || 'International'}`.trim()}
              </Text>
            </View>
          )}
          {urgency && (
            <View style={[styles.badge, styles.badgeUrgent]}>
              <Text style={[styles.badgeText, styles.badgeUrgentText]}>
                Urgent
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* Content */}
      <View style={styles.content}>
        <Text style={styles.title}>{title}</Text>
        {subtitle && (
          <Text style={styles.subtitle}>({subtitle})</Text>
        )}
        {specialization && (
          <Text style={styles.specialization}>{specialization}</Text>
        )}
        {description && (
          <Text style={styles.description} numberOfLines={2}>
            {description}
          </Text>
        )}
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <View style={styles.footerLeft}>
          {salary && <Text style={styles.salary}>{salary}</Text>}
          {job.international && (
            <Text style={styles.openTo}>
              {`Open to: ${getWorldCountryData(job.international)?.flag || ''} ${getWorldCountryData(job.international)?.name || job.international}`.trim()}
            </Text>
          )}
          <Text style={styles.postedDate}>{formatDate(postedDate)}</Text>
        </View>
        <View style={styles.footerRight}>
          {applicants !== undefined && (
            <Text style={styles.applicants}>
              {applicants} applicant{applicants !== 1 ? 's' : ''}
            </Text>
          )}
          {showApplyButton && (
            <Pressable
              onPress={(e) => {
                e.stopPropagation();
                onApply?.(job);
              }}
              style={({ hovered }) => [
                styles.applyButton,
                hovered && styles.applyButtonHover,
              ]}
            >
              <Text style={styles.applyButtonText}>Apply</Text>
            </Pressable>
          )}
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    ...webShadows.card,
    padding: 20,
    marginBottom: 16,
    transitionDuration: '200ms',
    transitionProperty: 'box-shadow, transform',
  },
  cardCompact: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.small,
    padding: 12,
    borderWidth: 1,
    borderColor: webColors.border,
    transitionDuration: '200ms',
  },
  cardHover: {
    ...webShadows.cardHover,
    transform: [{ translateY: -2 }],
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  facilityInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  facilityLogo: {
    width: 48,
    height: 48,
    borderRadius: webBorderRadius.small,
    marginRight: 12,
  },
  facilityLogoPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: webBorderRadius.small,
    backgroundColor: webColors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  facilityLogoText: {
    color: '#ffffff',
    fontSize: 20,
    fontWeight: '600',
  },
  facilityDetails: {
    flex: 1,
  },
  facilityName: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  location: {
    fontSize: 13,
    color: webColors.textSecondary,
  },
  badges: {
    flexDirection: 'row',
    gap: 8,
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  badgeLocum: {
    backgroundColor: '#dbeafe',
  },
  badgeFullTime: {
    backgroundColor: '#dcfce7',
  },
  badgePartTime: {
    backgroundColor: '#fef3c7',
  },
  badgeDefault: {
    backgroundColor: webColors.background,
  },
  badgeInternational: {
    backgroundColor: '#e0e7ff',
  },
  badgeUrgent: {
    backgroundColor: '#fee2e2',
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '500',
    color: webColors.textPrimary,
  },
  badgeUrgentText: {
    color: '#dc2626',
  },
  content: {
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: webColors.textSecondary,
    marginBottom: 8,
  },
  specialization: {
    fontSize: 14,
    color: webColors.secondary,
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 22,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: webColors.border,
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  salary: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.success,
  },
  openTo: {
    fontSize: 13,
    color: webColors.textSecondary,
  },
  postedDate: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  footerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  applicants: {
    fontSize: 13,
    color: webColors.textSecondary,
  },
  applyButton: {
    backgroundColor: webColors.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: webBorderRadius.small,
    transitionDuration: '200ms',
  },
  applyButtonHover: {
    backgroundColor: webColors.primaryDark,
  },
  applyButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Compact variant styles
  compactContent: {
    gap: 4,
  },
  compactTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  compactFacility: {
    fontSize: 13,
    color: webColors.textSecondary,
  },
  compactMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  compactLocation: {
    fontSize: 12,
    color: webColors.textMuted,
  },
});

export default WebJobCard;
